<script lang="ts">
// import type { FeatureCollection } from 'geojson' // FeatureCollection is not defined !!:!! ARGGHHH
// import type { ProjectionLike } from 'ol/proj'  // ProjectionLike is not defined !!:!! ARGGHHH
import { GeoJSON } from 'ol/format'
import { inject } from 'vue'
import type Map from 'ol/Map'
import type { MapBrowserEvent } from 'ol'
import type { FeatureLike } from 'ol/Feature'
import type VectorLayer from 'ol/layer/Vector'
import type VectorSource from 'ol/source/Vector'
import type { Extent } from 'ol/extent'
import { getCenter, intersects } from 'ol/extent'
import type { SimpleGeometry } from 'ol/geom'
import { Point } from 'ol/geom'
import type { GeoJSONGeometry } from 'ol/format/GeoJSON'
import { singleClick } from 'ol/events/condition'
import { VectorLayerService } from '../../../services/vectorLayer.service'
import DkOlVectorLayerEditor from '../controls/dk-ol-vector-layer-editor.vue'

/* eslint no-console: ["error", { allow: ["info","debug","warn"] }] */
// noinspection JSUnusedGlobalSymbols
export default {
    name: 'DkOlVectorLayer',
    components: { DkOlVectorLayerEditor },
    props: {
        title: {
            type: String,
            required: true,
        },
        zIndex: {
            type: Number,
            required: false,
            default: 1,
        },
        geojsonData: {
            // type: FeatureCollection,
            type: Object,
            required: true,
        },
        vectorLayerService: {
            type: VectorLayerService,
            required: false,
            default: new VectorLayerService(),
        },
        selectedFeatures: {
            type: Array<FeatureLike>,
            required: false,
            default: [],
        },
        editSelected: {
            type: Boolean,
            required: false,
            default: false,
        },
        create: {
            type: Boolean,
            required: false,
            default: false,
        },
        createType: {
            // type: GeometryType,
            type: String,
            required: false,
            default: '',
        },
        allowMultipleGeometry: {
            type: Boolean,
            required: false,
            default: false,
        },
        dataProjection: {
            // type: ProjectionLike,
            type: String,
            required: false,
            default: 'EPSG:4326', // reproject 4326 to 3857
        },
        displayGeomAsCentroidPoint: {
            type: Boolean,
            required: false,
            default: false,
        },
        editionTitle: {
            type: String,
            required: false,
            default: 'Édition',
        },
        showOnMount: {
            type: Boolean,
            required: false,
            default: true,
        },
        webgl: {
            type: Boolean,
            required: false,
            default: false,
        },
        webglStyle: {
            type: Object,
            required: false,
            default: {
                'stroke-width': 5,
                'stroke-color': 'rgba(24,86,34,0.4)',
                'fill-color': '#0000ff40',
            },
        },
    },
    emits: ['hoverFeatures', 'clickFeatures', 'updateGeometry', 'loadJsonDone'],
    data() {
        return {
            map: null as Map,
            isHoverFeature: false as boolean,
            geoJson: null as GeoJSON,
            geoJsonFeatures: [] as FeatureLike[],
            highlightedFeatures: [] as FeatureLike[],
            editFeatures: [] as FeatureLike[],
            editFeaturesBackup: [] as FeatureLike[],
            pointerMoveTimeout: null,
        }
    },
    computed: {
        layer(): VectorLayer {
            return this.getLayer()
        },
        layerSource(): VectorSource {
            return this.getLayer()?.getSource() as VectorSource
        },
    },
    watch: {
        selectedFeatures() {
            this.refreshFeatureStates()
        },
        editSelected() {
            // this.loadJson()
            this.saveOriginalEditFeatures()
            this.refreshFeatureStates()
        },
        geojsonData() {
            this.loadJson()
            this.refreshFeatureStates()
        },
        displayGeomAsCentroidPoint() {
            this.loadJson()
            this.refreshFeatureStates()
        },
        geoJsonFeatures() {
            this.$nextTick(() => {
                this.$emit('loadJsonDone', this.geoJsonFeatures)
            })
        },
    },
    mounted() {
        this.map = inject('map') as Map
        // hover event
        this.map.on('pointermove', (event: MapBrowserEvent<PointerEvent>) => {
            this.onPointerMoveDebounce(event)
        })

        this.initClickEvent()
        this.loadJson()
    },
    methods: {
        getLayer(): VectorLayer {
            return this.$refs?.layer?.vectorLayer as VectorLayer
        },
        initClickEvent() {
            this.map.on('click', (event: MapBrowserEvent<PointerEvent>) => {
                // console.time("click init");
                const clickFeatures = this.map.getFeaturesAtPixel(event.pixel, {
                    hitTolerance: 2,
                    condition: singleClick,
                    // if several vector layers on the same map => search on own layer. this.layer has to be not undefined
                    layerFilter: (layerCandidate: Layer) => {
                        return this.getLayer()?.ol_uid === layerCandidate.ol_uid
                    },
                })
                // console.timeLog("click init");

                if (clickFeatures?.length > 0) {
                    this.$emit('clickFeatures', clickFeatures)
                }
            })
        },
        onPointerMoveDebounce(event: MapBrowserEvent<PointerEvent>): void {
            if (this.pointerMoveTimeout) {
                clearTimeout(this.pointerMoveTimeout)
            }
            this.pointerMoveTimeout = setTimeout(() => {
                const hoverFeatures = this.map.getFeaturesAtPixel(event.pixel, {
                    // hitTolerance: 10,
                    layerFilter: (layerCandidate: Layer) => {
                        return this.layer?.ol_uid === layerCandidate.ol_uid
                    },
                })

                this.isHoverFeature = hoverFeatures.length > 0

                if (JSON.stringify(hoverFeatures) !== JSON.stringify(this.highlightedFeatures)) {
                    this.highlightedFeatures = hoverFeatures
                    this.$emit('hoverFeatures', this.highlightedFeatures)
                    this.vectorLayerService.setHighlightedFeatures(this.highlightedFeatures)
                    this.redraw()
                }
            }, 50)
        },

        refreshFeatureStates(updateEditing: boolean = false): void {
            if (!updateEditing && this.vectorLayerService.isEditing() && this.editSelected) {
                // don't change state if the feature is already editing !important to keep edition unchanged when style is changed
                return
            }
            this.vectorLayerService.setHideSelected(this.editSelected)

            if (this.selectedFeatures.length && this.selectedFeatures[0]) {
                this.editFeatures = this.cloneAndExplodeFeature(this.selectedFeatures[0])
            }
            this.saveOriginalCoordinates()

            this.vectorLayerService.setSelectedFeatures(this.selectedFeatures)
            this.redraw()
        },

        loadJson(): void {
            this.geoJson = new GeoJSON()
            this.geoJsonFeatures = this.geoJson.readFeatures(this.geojsonData, {
                dataProjection: this.dataProjection, // reproject dataProjection to mapProjection
                featureProjection: this.map.getView().getProjection(),
            })

            if (this.displayGeomAsCentroidPoint) {
                this.geoJsonFeatures.forEach((feature: FeatureLike) => {
                    const geometry: SimpleGeometry = feature.getGeometry()
                    if (!geometry.getType().endsWith('Point')) {
                        // save original geometry Type
                        this.vectorLayerService.saveFeatureOriginalGeometryType(feature)
                        // create point at geometry center
                        const centerPoint = new Point(getCenter(geometry.getExtent()))
                        feature.setGeometry(centerPoint)
                    }
                })
            }
        },

        displayAll(maxZoom: number = 15): void {
            const view = this.map.getView()
            const options = {
                duration: 1000,
                padding: [80, 80, 80, 80], // pixels
            }
            if (maxZoom) {
                options.maxZoom = maxZoom
            }
            if (this.layerSource?.getFeatures()?.length) { // To avoid "Cannot fit empty extent provided as `geometry`", check if features in layer
                view.fit(this.layerSource.getExtent(), options)
            }
        },

        centerOnFeature(feature: FeatureLike, zoomToExtends: boolean = false, maxZoom: number = 15): void {
            if (feature) {
                const featureExtends = feature.getGeometry().getExtent()
                if (isFinite(featureExtends[0])) {
                    const view = this.map.getView()
                    if (zoomToExtends) {
                        view.fit(featureExtends, { duration: 1000, maxZoom })
                    }
                    else {
                        const center = getCenter(featureExtends)
                        view.animate({ center, duration: 1000 })
                    }
                }
            }
        },

        centerOnFeatureIfOutOfScope(feature: FeatureLike): void {
            const mapExtent = this.map.getView().calculateExtent() as Extent
            const featureExtent = feature.getGeometry().getExtent() as Extent
            if (!intersects(mapExtent, featureExtent)) {
                this.centerOnFeature(feature)
            }
        },

        getFeatureByKey(k: string): FeatureLike | null {
            const features = this.getFeaturesByKey(k)

            if (!features || features.length === 0) {
                console.warn(`feature ${k} not found`)
                return null
            }

            return features[0]
        },

        getFeaturesByKey(k: string): FeatureLike[] {
            return this.layerSource?.getFeatures().filter((feature: FeatureLike) => {
                return feature.get('k') === k
            })
        },

        redraw(): void {
            this.getLayer()?.changed()
        },

        resetCoordinates(): void {
            this.selectedFeatures.forEach((feature: FeatureLike) => {
                if (feature.get('originalCoordinates')) {
                    (feature.getGeometry() as SimpleGeometry).setCoordinates(feature.get('originalCoordinates'))
                }
            })

            this.editFeatures = []

            this.editFeaturesBackup.forEach((feature: FeatureLike) => {
                this.editFeatures.push(feature.clone())
            })
        },

        saveOriginalEditFeatures(): void {
            this.editFeaturesBackup = []
            this.editFeatures.forEach((feature: FeatureLike) => {
                this.editFeaturesBackup.push(feature.clone())
            })
        },

        saveOriginalCoordinates(): void {
            this.selectedFeatures.forEach((feature: FeatureLike) => {
                // save original coordinates
                feature.set('originalCoordinates', (feature.getGeometry() as SimpleGeometry).getCoordinates())
            })
        },

        updateGeometry(geometry: SimpleGeometry | null): void {
            if (!this.create) {
                // update selected feature
                (this.selectedFeatures[0]?.getGeometry() as SimpleGeometry).setCoordinates(geometry ? geometry.getCoordinates() : [])
            }
            let reprojectedGeometry: GeoJSONGeometry = null
            if (geometry) {
                reprojectedGeometry = (new GeoJSON({
                    dataProjection: this.dataProjection,
                    featureProjection: this.map.getView().getProjection(),
                })).writeGeometryObject(geometry)
            }

            this.$emit('updateGeometry', { geometry, reprojectedGeometry })
        },

        cloneAndExplodeFeature(originalFeature: FeatureLike): FeatureLike[] {
            return this.vectorLayerService.cloneAndExplodeFeature(originalFeature)
        },
    },
}
</script>

<template>
    <template v-if="geoJson">
        <ol-vector-layer ref="layer" :title :z-index :visible="showOnMount">
            <ol-source-vector
                :features="geoJsonFeatures"
                :format="geoJson"
            >
                <slot name="default" />
            </ol-source-vector>
        </ol-vector-layer>

        <!-- edition layer -->
        <ol-vector-layer v-if="editSelected && geoJson" :z-index :title="editionTitle">
            <ol-source-vector :features="create ? [] : editFeatures">
                <slot name="edition-layer" />
                <slot name="edition-layer-controller">
                    <DkOlVectorLayerEditor
                        ref="layerEditor"
                        :snap-source="getLayer()?.getSource()"
                        :create
                        :create-type
                        :allow-multiple-geometry
                        :vector-layer-service
                        @update-geometry="updateGeometry($event)"
                        @reset-geometry="resetCoordinates()"
                    />
                </slot>
            </ol-source-vector>
        </ol-vector-layer>
    </template>
</template>

<style>

</style>
